import { MessageContext } from '@snapchat/snap-design-system-marketing';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import type { ShopifyAccess } from '../../configTypes';
import type { ProductAvailability, ShopifyProviderProps } from '../Cart/types';
import { Feature, useFeatureFlags } from '../FeatureFlags';
import type { CatalogProduct, CatalogState } from './catalogTypes';
import { normalizeCatalog } from './normalizeCatalog';

const defaultCatalogState: CatalogState = {
  catalog: {},
  productOptionById: {},
};

export const ShopifyContext = createContext<CatalogState>(defaultCatalogState);

export const ShopifyProvider: FC<PropsWithChildren<ShopifyProviderProps>> = ({
  children,
  shopifyData,
}) => {
  const {
    userLocation: { country },
  } = useContext(AppContext);
  const { formatMessage } = useContext(MessageContext);
  const featureFlags = useFeatureFlags();

  const calculateAtp = useCallback(
    ({ available, atp = 0 }: ProductAvailability): string | null => {
      if (atp === 0 && available) {
        return formatMessage({
          id: 'shopifyATPTodayFreeShipping',
          defaultMessage: 'Ships for free today',
        });
      }

      if (atp > 0 && available) {
        if (atp < 7) {
          if (atp === 1) {
            return formatMessage({
              id: 'shopifyATPDayFreeShipping',
              defaultMessage: 'Ships for free in 1 day',
            });
          }

          return formatMessage(
            { id: 'shopifyATPDaysFreeShipping', defaultMessage: 'Ships for free in {days} days' },
            { days: String(atp) }
          );
        }
        const bufferRemainder = atp % 7;
        const weeks = (atp - bufferRemainder) / 7;
        const weekRange = `${weeks}-${weeks + 1}`;

        if (weeks === 1) {
          return formatMessage({
            id: 'shopifyATPWeekFreeShipping',
            defaultMessage: 'Ships for free in 1 week',
          });
        }

        return formatMessage(
          {
            id: 'shopifyATPWeeksFreeShipping',
            defaultMessage: 'Ships for free in {weeks} weeks',
          },
          { weeks: weekRange }
        );
      }

      return null;
    },
    [formatMessage]
  );

  if (!Config.shopify) {
    return <>{children}</>;
  }

  const { catalog, productOptionById } = normalizeCatalog(shopifyData, calculateAtp);

  // Store selection
  const singleStoreMode = Config.shopify?.singleStoreMode === true;

  let store: ShopifyAccess | undefined;

  if (singleStoreMode) {
    // Single store mode: use the one configured store for all countries.
    // Market-aware pricing is handled server-side via @inContext(country:).
    const storeKeys = Object.keys(Config.shopify?.stores ?? {});
    const singleStore = Config.shopify!.stores[storeKeys[0]!];
    store = singleStore?.[Config.deploymentType];
  } else {
    const enableEuropeanStores = featureFlags[Feature.ENABLE_PIXY_EUROPEAN_STORES] === 'true';
    const isHiddenBehindFeatureFlag =
      !enableEuropeanStores && (country === 'GB' || country === 'FR');
    const shopifyStore = Config.shopify?.stores[country];

    store =
      shopifyStore && !isHiddenBehindFeatureFlag ? shopifyStore[Config.deploymentType] : undefined;
  }

  // Use the currency from product data (reflects @inContext market pricing)
  // rather than shop.paymentSettings.currencyCode (always the store's base currency).
  const firstProduct = Object.values(catalog)[0] as CatalogProduct | undefined;
  const currencyCode = firstProduct?.currencyCode ?? shopifyData?.shop.paymentSettings.currencyCode;

  // Determine if the store ships to the user's country. In singleStoreMode,
  // `store` is always set regardless of country, so we also check shipsToCountries.
  // `undefined` means data isn't available — consumers should treat this permissively.
  const shipsToCountries = shopifyData?.shop?.shipsToCountries;
  const isCountrySupported = shipsToCountries ? shipsToCountries.includes(country) : undefined;

  return (
    <ShopifyContext.Provider
      value={{
        catalog,
        productOptionById,
        store,
        currencyCode,
        shopData: shopifyData?.shop,
        rawShopifyResponse: shopifyData,
        isCountrySupported,
      }}
    >
      {children}
    </ShopifyContext.Provider>
  );
};
